import { createFileRoute } from '@tanstack/react-router'
import type { ReactElement } from 'react'
import { z } from 'zod'

import { AiBudgetPanel, TeamBudgetDetail, type AiBudgetStateFilter } from '~/features/ai-budget'
import { useUrlState } from '~/lib/query/use-url-state'

/**
 * `/admin/ai-budget` — the AI Budget admin surface (Packet A1).
 *
 * URL search keys: `window` (usage look-back, days), `state` (seat-state
 * filter), `teamId` (drills into one team's detail — opening a row sets it,
 * closing the detail clears it). Same URL-as-source-of-truth pattern as
 * `/admin/memory-health`, so an operator can link a colleague to the exact
 * view — including which team they had open.
 */
const STATE_VALUES = [
  'all',
  'ok',
  'near_limit',
  'exhausted',
  'admin_unlocked',
  'unverified',
] as const

const aiBudgetSearchSchema = z.object({
  window: z.coerce
    .number()
    .int()
    .refine((value) => [7, 30, 90].includes(value), {
      message: 'window must be 7, 30 or 90',
    })
    .default(30),
  state: z.enum(STATE_VALUES).default('all'),
  teamId: z.string().uuid().optional(),
})
type AiBudgetSearch = z.infer<typeof aiBudgetSearchSchema>

const URL_DEFAULTS: AiBudgetSearch = { window: 30, state: 'all' }
const WINDOWS = [7, 30, 90] as const

export const Route = createFileRoute('/admin/ai-budget')({
  validateSearch: aiBudgetSearchSchema,
  component: AiBudgetRoute,
})

function AiBudgetRoute(): ReactElement {
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(aiBudgetSearchSchema, URL_DEFAULTS)

  return (
    <div className="ka-page ka-ai-budget-page">
      <header className="ka-page-head">
        <div>
          <h1>AI Budget</h1>
          <p>
            Seats at or above 80% of their effective AI budget across every tenant. Apply or
            revoke overrides, grant top-ups, and toggle organization PAYG.
          </p>
        </div>
        <label className="ka-field">
          <span>Window</span>
          <select
            value={search.window}
            onChange={(event) => setUrl({ window: Number(event.target.value) as 7 | 30 | 90 })}
          >
            {WINDOWS.map((days) => (
              <option key={days} value={days}>
                Last {days} days
              </option>
            ))}
          </select>
        </label>
      </header>

      <AiBudgetPanel
        windowDays={search.window}
        stateFilter={search.state as AiBudgetStateFilter}
        onStateFilterChange={(state) => setUrl({ state })}
        onSelectTeam={(teamId) => setUrl({ teamId })}
      />

      {search.teamId ? (
        <TeamBudgetDetail teamId={search.teamId} onClose={() => setUrl({ teamId: undefined })} />
      ) : null}
    </div>
  )
}
